import { getCurrencyMinorUnits } from "../currency/currencyPrecision";

export const DRAFT_GROUP_RATE_APPLY_PATH =
  "/api/reservation-draft-group-rates/apply";

export const MAX_GROUP_RATE_AMOUNT = 99_999_999.99;

const positiveInteger = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const resolveDraftGroupExtrasContext = ({
  groupId,
  businessUnitId,
} = {}) => {
  const normalizedGroupId = positiveInteger(groupId);
  if (!normalizedGroupId) {
    return {
      valid: false,
      error: "A valid draft group is required to manage group extras.",
    };
  }

  const normalizedBusinessUnitId = positiveInteger(businessUnitId);
  if (!normalizedBusinessUnitId) {
    return {
      valid: false,
      error: "Select an active hotel before managing group extras.",
    };
  }

  return {
    valid: true,
    groupId: normalizedGroupId,
    businessUnitId: normalizedBusinessUnitId,
    error: "",
  };
};

export const createLatestRequestTracker = () => {
  let latestToken = 0;
  return Object.freeze({
    begin() {
      latestToken += 1;
      return latestToken;
    },
    invalidate() {
      latestToken += 1;
      return latestToken;
    },
    isCurrent(token) {
      return token === latestToken;
    },
  });
};

const nonEmptyText = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const normalizedCurrency = (value) => {
  const currency = nonEmptyText(value).toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : "currency not configured";
};

const displayAmount = (value, currency, minorUnits) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "invalid amount";
  const currencyCatalog = minorUnits === undefined
    ? []
    : [{ code: currency, minor_units: minorUnits }];
  return amount.toFixed(getCurrencyMinorUnits(currency, currencyCatalog));
};

export const formatDistributedGroupExtraDescription = (extra = {}, languageId = "en") => {
  const currency = normalizedCurrency(extra.currency);
  const isRelative = String(extra.amount_type || "").trim().toLowerCase() === "relative";
  const amount = isRelative
    ? displayAmount(extra.amount, "")
    : displayAmount(extra.amount, currency, extra.minor_units);
  if (languageId === "es") {
    const currencyLabel = currency === "currency not configured" ? "moneda sin configurar" : currency;
    const amountLabel = amount === "invalid amount" ? "importe no válido" : amount.replace(".", ",");
    return isRelative
      ? `${amountLabel}% sobre la base del grupo en ${currencyLabel}; es un único total, distribuido entre las reservas`
      : `${amountLabel} ${currencyLabel} en total para el grupo, distribuido entre las reservas`;
  }
  if (isRelative) {
    return `${amount}% of the group basis in ${currency}; this is one total for the group, distributed across reservations`;
  }
  return `${amount} ${currency} total for the group, distributed across reservations`;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeGroupExtraRecord = (row) => {
  if (!row || typeof row !== "object") return null;
  const nested = row.extra && typeof row.extra === "object" ? row.extra : {};
  const id = positiveInteger(
    firstDefined(nested.id, row.extra_id, row.extraId, row.id)
  );
  if (!id) return null;
  return { ...row, ...nested, id };
};

export const getGroupExtraUnavailableReason = (extra = {}) => {
  if (String(extra.scope || "").trim() !== "per_reservation") {
    return {
      code: "invalid_scope",
      message: "not available for group reservations",
    };
  }

  if (extra.is_active !== true) {
    return {
      code: "inactive",
      message: "inactive",
    };
  }

  if (extra.inclusive === true) {
    return {
      code: "inclusive",
      message: "already included in the reservation price",
    };
  }

  const amount = Number(extra.amount);
  if (!Number.isFinite(amount)) {
    return {
      code: "invalid_amount",
      message: "amount is not valid",
    };
  }

  if (Math.round(amount * 100) === 0) {
    return {
      code: "zero_amount",
      message: "amount must not be zero",
    };
  }

  return null;
};

export const isSelectableGroupExtra = (extra = {}) =>
  getGroupExtraUnavailableReason(extra) === null;

export const getSelectableGroupExtras = (catalog = []) => {
  const seen = new Set();
  return asArray(catalog)
    .map(normalizeGroupExtraRecord)
    .filter(Boolean)
    .filter((extra) => {
      if (seen.has(extra.id) || !isSelectableGroupExtra(extra)) return false;
      seen.add(extra.id);
      return true;
    });
};

const mergeAppliedGroupExtras = ({
  catalog = [],
  applied = [],
} = {}) => {
  const catalogById = new Map(
    asArray(catalog)
      .map(normalizeGroupExtraRecord)
      .filter(Boolean)
      .map((extra) => [extra.id, extra])
  );
  const seen = new Set();
  const selected = [];

  asArray(applied).forEach((row) => {
    const appliedExtra = normalizeGroupExtraRecord(row);
    if (!appliedExtra || seen.has(appliedExtra.id)) return;
    seen.add(appliedExtra.id);
    selected.push({
      ...appliedExtra,
      ...(catalogById.get(appliedExtra.id) || {}),
      id: appliedExtra.id,
    });
  });

  return selected;
};

export const buildInitialGroupExtraSelection = (input = {}) =>
  mergeAppliedGroupExtras(input).filter(isSelectableGroupExtra);

export const getUnavailableAppliedGroupExtras = (input = {}) =>
  mergeAppliedGroupExtras(input).filter(
    (extra) => !isSelectableGroupExtra(extra)
  );

export const getAddableGroupExtras = (catalog = [], selected = []) => {
  const selectedIds = new Set(
    asArray(selected)
      .map((extra) => positiveInteger(extra?.id))
      .filter(Boolean)
  );
  return getSelectableGroupExtras(catalog).filter(
    (extra) => !selectedIds.has(extra.id)
  );
};

export const buildGroupExtrasAssignmentPayload = (selected = []) => ({
  extras: getSelectableGroupExtras(selected).map((extra) => ({
    extra_id: extra.id,
  })),
});

const resultArrays = (payload = {}) => {
  const result = payload?.result && typeof payload.result === "object"
    ? payload.result
    : {};
  const appliedCandidates = [
    payload?.applied,
    payload?.applied_extras,
    result.applied,
    result.applied_extras,
  ];
  const rejectedCandidates = [
    payload?.rejected,
    payload?.failed,
    payload?.rejected_extras,
    result.rejected,
    result.failed,
    result.rejected_extras,
  ];
  return {
    applied:
      appliedCandidates.find((candidate) => Array.isArray(candidate)) || [],
    rejected:
      rejectedCandidates.find((candidate) => Array.isArray(candidate)) || [],
  };
};

const uniquePositiveIds = (values) => [
  ...new Set(asArray(values).map(positiveInteger).filter(Boolean)),
];

const resultExtraId = (row) => {
  if (typeof row === "number" || typeof row === "string") {
    return positiveInteger(row);
  }
  return positiveInteger(
    firstDefined(row?.extra_id, row?.extraId, row?.extra?.id, row?.id)
  );
};

const normalizeResultCurrency = (value) => {
  const currency = nonEmptyText(value).toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : "";
};

const selectedExtrasById = (selected = []) =>
  new Map(
    asArray(selected)
      .map(normalizeGroupExtraRecord)
      .filter(Boolean)
      .map((extra) => [extra.id, extra])
  );

const normalizeAppliedGroupExtra = (row, selectedById) => {
  const source = row && typeof row === "object" ? row : { extra_id: row };
  const id = resultExtraId(source);
  if (!id) return null;
  const selected = selectedById.get(id) || {};
  const details = source.details && typeof source.details === "object"
    ? source.details
    : {};
  return {
    extra_id: id,
    name: nonEmptyText(source.name) || nonEmptyText(source.extra?.name) || selected.name || `Extra ${id}`,
    amount_type: String(
      firstDefined(source.amount_type, source.extra?.amount_type, selected.amount_type) || ""
    ).toLowerCase(),
    source_amount: firstDefined(
      source.source_amount,
      source.original_amount,
      details.source_amount,
      selected.amount
    ),
    source_currency: normalizeResultCurrency(
      firstDefined(
        source.source_currency,
        source.source_currency_code,
        source.original_currency,
        details.source_currency,
        selected.currency
      )
    ),
    source_minor_units: firstDefined(
      source.source_minor_units,
      details.source_minor_units,
      selected.minor_units
    ),
    base_amount: firstDefined(
      source.base_amount,
      source.converted_amount,
      source.calculated_amount,
      details.base_amount
    ),
    base_currency: normalizeResultCurrency(
      firstDefined(
        source.base_currency,
        source.base_currency_code,
        source.currency,
        details.base_currency
      )
    ),
    base_minor_units: firstDefined(
      source.base_minor_units,
      details.base_minor_units
    ),
    fx_rate_set_id: firstDefined(source.fx_rate_set_id, details.fx_rate_set_id),
    applied_rate: firstDefined(source.applied_rate, details.applied_rate),
  };
};

const normalizeRejectedGroupExtra = (row, selectedById) => {
  const source = row && typeof row === "object" ? row : { extra_id: row };
  const id = resultExtraId(source);
  if (!id) return null;
  const selected = selectedById.get(id) || {};
  const details = source.details && typeof source.details === "object"
    ? source.details
    : null;
  const code = nonEmptyText(source.code) || nonEmptyText(source.error_code);
  const reason =
    nonEmptyText(source.reason) ||
    nonEmptyText(source.message) ||
    nonEmptyText(source.error) ||
    code ||
    "This extra could not be applied.";
  return {
    extra_id: id,
    name: nonEmptyText(source.name) || selected.name || `Extra ${id}`,
    code,
    reason,
    details,
  };
};

const appliedResultsFromIds = ({ ids, rows, selectedById }) =>
  uniquePositiveIds(ids).map((id) => {
    const matchingRows = asArray(rows).filter(
      (row) => resultExtraId(row) === id
    );
    const normalized = normalizeAppliedGroupExtra(
      matchingRows[0] || { extra_id: id },
      selectedById
    );
    if (!normalized || matchingRows.length === 0) return normalized;

    const hasExplicitBaseAmount = matchingRows.some(
      (row) =>
        row?.base_amount !== undefined || row?.converted_amount !== undefined
    );
    if (!hasExplicitBaseAmount) {
      const calculatedAmounts = matchingRows.map((row) =>
        Number(row?.calculated_amount)
      );
      if (calculatedAmounts.every(Number.isFinite)) {
        normalized.base_amount = calculatedAmounts.reduce(
          (sum, amount) => sum + amount,
          0
        );
      }
    }
    return normalized;
  });

export const hasStructuredGroupExtraAssignmentResult = (payload = {}) => {
  const result = payload?.result && typeof payload.result === "object"
    ? payload.result
    : {};
  const outcome = nonEmptyText(payload?.outcome) || nonEmptyText(result.outcome);
  if (["full_success", "partial_success", "none_applied"].includes(outcome)) {
    return true;
  }
  return [
    payload?.applied,
    payload?.applied_extras,
    payload?.rejected,
    payload?.failed,
    payload?.rejected_extras,
    result.applied,
    result.applied_extras,
    result.rejected,
    result.failed,
    result.rejected_extras,
    payload?.applied_extra_ids,
    payload?.preserved_extra_ids,
    payload?.removed_extra_ids,
    payload?.selected_extra_ids_after,
    result.applied_extra_ids,
    result.preserved_extra_ids,
    result.removed_extra_ids,
    result.selected_extra_ids_after,
  ].some((candidate) => Array.isArray(candidate));
};

export const normalizeGroupExtraAssignmentResult = (
  payload = {},
  selected = []
) => {
  const arrays = resultArrays(payload);
  const selectedById = selectedExtrasById(selected);
  const rows = asArray(payload?.rows ?? payload?.result?.rows);
  const appliedExtraIds = uniquePositiveIds(
    payload?.applied_extra_ids ?? payload?.result?.applied_extra_ids
  );
  const appliedById = new Map();
  arrays.applied
    .map((row) => normalizeAppliedGroupExtra(row, selectedById))
    .filter(Boolean)
    .forEach((row) => appliedById.set(row.extra_id, row));
  appliedResultsFromIds({
    ids: appliedExtraIds,
    rows,
    selectedById,
  })
    .filter(Boolean)
    .forEach((row) => {
      if (!appliedById.has(row.extra_id)) appliedById.set(row.extra_id, row);
    });
  const applied = [...appliedById.values()];
  const rejected = arrays.rejected
    .map((row) => normalizeRejectedGroupExtra(row, selectedById))
    .filter(Boolean);
  const removedIds = uniquePositiveIds(
    payload?.removed_extra_ids ?? payload?.result?.removed_extra_ids
  );
  const preservedIds = uniquePositiveIds(
    payload?.preserved_extra_ids ?? payload?.result?.preserved_extra_ids
  );
  const selectedIdsAfter = uniquePositiveIds(
    payload?.selected_extra_ids_after ?? payload?.result?.selected_extra_ids_after
  );
  const outcome = nonEmptyText(payload?.outcome) ||
    nonEmptyText(payload?.result?.outcome);

  return {
    outcome,
    partial_success:
      outcome === "partial_success" ||
      payload?.partial_success === true ||
      payload?.result?.partial_success === true ||
      (applied.length > 0 && rejected.length > 0),
    applied,
    rejected,
    applied_extra_ids:
      appliedExtraIds.length > 0
        ? appliedExtraIds
        : applied.map((item) => item.extra_id),
    preserved_extra_ids: preservedIds,
    removed_extra_ids: removedIds,
    selected_extra_ids_after: selectedIdsAfter,
    totals: payload?.totals ?? payload?.result?.totals ?? null,
    rows,
  };
};

export const isPartialGroupExtraAssignmentResult = (
  result,
  httpStatus
) =>
  Number(httpStatus) === 207 ||
  result?.outcome === "partial_success" ||
  result?.outcome === "none_applied" ||
  result?.partial_success === true ||
  asArray(result?.rejected).length > 0;

const formatResultAmount = (value, currency, minorUnits) => {
  if (value === undefined || value === null || value === "") return "";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  const suffix = normalizeResultCurrency(currency);
  return `${displayAmount(amount, suffix, minorUnits)}${suffix ? ` ${suffix}` : ""}`;
};

export const formatAppliedGroupExtraResult = (result = {}, languageId = "en") => {
  const source =
    result.amount_type === "relative"
      ? Number.isFinite(Number(result.source_amount))
        ? `${Number(result.source_amount).toFixed(2)}%`
        : ""
      : formatResultAmount(
          result.source_amount,
          result.source_currency,
          result.source_minor_units
        );
  const base = formatResultAmount(
    result.base_amount,
    result.base_currency,
    result.base_minor_units
  );
  const label = languageId === "es" ? "Aplicado" : "Applied";
  if (source && base) return `${label}: ${source} → ${base}`;
  if (base) return `${label}: ${base}`;
  if (source) return `${label}: ${source}`;
  return label;
};

export const formatRejectedGroupExtraResult = (result = {}, languageId = "en") => {
  const reason = nonEmptyText(result.reason) || "This extra could not be applied.";
  const code = nonEmptyText(result.code);
  if (languageId === "es") {
    const reasons = {
      invalid_scope: "No está disponible para reservas de grupo.",
      inactive: "El extra está inactivo.", inclusive: "Ya está incluido en el precio de la reserva.",
      invalid_amount: "El importe no es válido.", zero_amount: "El importe no puede ser cero.",
      fx_rate_not_found: "No hay un tipo de cambio disponible.",
      missing_fx_rate: "No hay un tipo de cambio disponible.",
    };
    const translated = reasons[code.toLowerCase()] || "No se pudo aplicar este extra. Revisa su configuración.";
    return code ? `${translated} (${code})` : translated;
  }
  return code && !reason.includes(code) ? `${reason} (${code})` : reason;
};

export const buildGroupExtraAssignmentFeedback = (payload = {}, languageId = "en") => {
  const result = normalizeGroupExtraAssignmentResult(payload);
  const appliedNames = result.applied.map(
    (item) => item.name || `Extra ${item.extra_id}`
  );
  const rejectedDescriptions = result.rejected.map(
    (item) =>
      `${item.name || `Extra ${item.extra_id}`}: ${formatRejectedGroupExtraResult(item, languageId)}`
  );
  if (languageId === "es") {
    const rejected = rejectedDescriptions.length > 0;
    return {
      title: rejected
        ? appliedNames.length > 0 ? "Extras del grupo actualizados parcialmente" : "No se han actualizado los extras del grupo"
        : "Extras del grupo actualizados",
      message: [
        appliedNames.length > 0 ? `Aplicados: ${appliedNames.join(", ")}.` : rejected ? "No se ha aplicado ningún extra seleccionado." : "Se han actualizado los extras del grupo.",
        rejected ? `No aplicados: ${rejectedDescriptions.join("; ")}.` : "",
        result.removed_extra_ids.length === 1 ? "Se ha quitado 1 extra no disponible o desmarcado." : result.removed_extra_ids.length > 1 ? `Se han quitado ${result.removed_extra_ids.length} extras no disponibles o desmarcados.` : "",
      ].filter(Boolean).join(" "),
      tone: rejected ? "warning" : "primary",
      result,
    };
  }
  const cleanupMessage = result.removed_extra_ids.length > 0
    ? `Removed ${result.removed_extra_ids.length} unavailable or deselected group ${result.removed_extra_ids.length === 1 ? "extra" : "extras"}.`
    : "";

  if (rejectedDescriptions.length > 0) {
    return {
      title: appliedNames.length > 0
        ? "Group extras partially updated"
        : "Group extras were not updated",
      message: [
        appliedNames.length > 0
          ? `Applied: ${appliedNames.join(", ")}.`
          : "No selected extras were applied.",
        `Not applied: ${rejectedDescriptions.join("; ")}.`,
        cleanupMessage,
      ].filter(Boolean).join(" "),
      tone: "warning",
      result,
    };
  }

  return {
    title: "Group extras updated",
    message: [
      appliedNames.length > 0
        ? `Applied: ${appliedNames.join(", ")}.`
        : "Group extras were updated.",
      cleanupMessage,
    ].filter(Boolean).join(" "),
    tone: "primary",
    result,
  };
};

export const getGroupExtraAssignmentPersistenceDecision = (payload = {}) => {
  const result = normalizeGroupExtraAssignmentResult(payload);
  const hasRejected = result.rejected.length > 0;
  const hasApplied =
    result.applied.length > 0 || result.applied_extra_ids.length > 0;
  const hasExplicitRemovals = result.removed_extra_ids.length > 0;
  const hasPersistedMutation = hasApplied || hasExplicitRemovals;

  return {
    result,
    hasRejected,
    hasApplied,
    hasExplicitRemovals,
    hasPersistedMutation,
    shouldCommitBackground: !hasRejected || hasPersistedMutation,
    shouldKeepOpen: hasRejected && !hasPersistedMutation,
  };
};

export const isCancelledGroupRateReservation = (reservation = {}) => {
  const cancellationFlag = reservation?.is_cancelled;
  const hasCancellationFlag =
    cancellationFlag === true ||
    cancellationFlag === 1 ||
    String(cancellationFlag ?? "").trim().toLowerCase() === "true";
  if (hasCancellationFlag) return true;

  const status = String(reservation.status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return status === "cancelled" || status === "canceled";
};

export const getActiveGroupRateReservationIds = (reservations = []) => {
  const ids = [];
  const seen = new Set();
  (Array.isArray(reservations) ? reservations : []).forEach((reservation) => {
    const id = positiveInteger(reservation?.id);
    if (!id || seen.has(id) || isCancelledGroupRateReservation(reservation)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

export const getGroupRateTargetReservations = ({
  reservations = [],
  scope = "group",
  selectedReservationIds = [],
} = {}) => {
  const activeReservations = (Array.isArray(reservations) ? reservations : [])
    .filter(
      (reservation) =>
        positiveInteger(reservation?.id) &&
        !isCancelledGroupRateReservation(reservation)
    );
  if (scope !== "per_reservation") return activeReservations;

  const selectedIds = new Set(
    (Array.isArray(selectedReservationIds) ? selectedReservationIds : [])
      .map(positiveInteger)
      .filter(Boolean)
  );
  return activeReservations.filter((reservation) =>
    selectedIds.has(Number(reservation.id))
  );
};

const hasAnyPermission = (permissions, permissionKeys) =>
  permissionKeys.some((permission) => permissions?.[permission] === true);

export const canAddDraftGroupRooms = (permissions = {}) =>
  hasAnyPermission(permissions, [
    "reservations.add_rooms",
    "reservations.create",
    "reservations.update",
  ]);

export const canManageDraftGroupExtras = (permissions = {}) =>
  hasAnyPermission(permissions, [
    "reservations.manage_extras",
    "reservations.create",
    "reservations.update",
  ]);

export const canManageGroupExtrasForContext = ({
  permissions = {},
  context = "unknown",
} = {}) => {
  if (context === "new_draft") {
    return canManageDraftGroupExtras(permissions);
  }

  if (context === "existing_group") {
    return hasAnyPermission(permissions, [
      "reservations.manage_extras",
      "reservations.update",
    ]);
  }

  return false;
};

export const buildDraftGroupAddRoomsPayload = ({
  reservationGroupId,
  newTotal,
  businessUnitId,
} = {}) => {
  const payload = {
    new_total: Number(newTotal),
    business_unit_id: Number(businessUnitId),
  };
  const normalizedGroupId = positiveInteger(reservationGroupId);
  if (normalizedGroupId) {
    payload.reservation_group_id = normalizedGroupId;
  }
  return payload;
};

export const getDraftGroupApiErrorMessage = (
  error,
  fallback = "The draft group operation could not be completed."
) => {
  const payload = error?.response?.data;
  const nestedError =
    payload?.error && typeof payload.error === "object"
      ? payload.error
      : null;
  const message =
    nonEmptyText(payload?.message) ||
    nonEmptyText(payload?.error) ||
    nonEmptyText(nestedError?.message) ||
    nonEmptyText(error?.message) ||
    fallback;
  const code =
    nonEmptyText(payload?.code) ||
    nonEmptyText(payload?.error_code) ||
    nonEmptyText(nestedError?.code);

  return code && !message.includes(code) ? `${message} (${code})` : message;
};

export const draftGroupRoomDeletePath = (groupId, draftId) => {
  const normalizedGroupId = positiveInteger(groupId);
  const normalizedDraftId = positiveInteger(draftId);
  if (!normalizedGroupId || !normalizedDraftId) return null;
  return `/api/reservation-draft-groups/${normalizedGroupId}/rooms/${normalizedDraftId}`;
};

export const draftGroupReservationsPath = (groupId) => {
  const normalizedGroupId = positiveInteger(groupId);
  return normalizedGroupId
    ? `/api/reservation-drafts/reservations/group/${normalizedGroupId}`
    : null;
};

export const buildDraftGroupRatePayload = (data = {}) => ({
  rateName: data.rateName,
  priceType: data.priceType,
  rateAmount: data.rateAmount,
  scope: data.scope,
  selectedReservationIds: data.selectedReservationIds,
  notes: data.notes,
});

export const isValidGroupRateAmount = (value, minorUnits = 2) => {
  const normalized = String(value ?? "").trim();
  const precision = Number(minorUnits);
  const safeMinorUnits = Number.isInteger(precision) && precision >= 0 && precision <= 6
    ? precision
    : 2;
  const amountPattern = safeMinorUnits === 0
    ? /^(?:\d+)$/
    : new RegExp(`^(?:\\d+)(?:\\.\\d{1,${safeMinorUnits}})?$`);
  if (!amountPattern.test(normalized)) return false;
  const amount = Number(normalized);
  return (
    Number.isFinite(amount) &&
    amount >= 0 &&
    amount <= MAX_GROUP_RATE_AMOUNT
  );
};
